import { useEffect, useRef, useState } from "react";

// Port of GlobalLink's useResponsiveTileTypography: a panel measures itself
// and picks a typography tier, so text scales with the space it is given.
// Consumers read `tier` (or the matching `data-tier` attribute in CSS).

const COMPACT_MAX_WIDTH = 520;
const COMPACT_MAX_HEIGHT = 360;
const COMFORTABLE_MIN_WIDTH = 820;
const COMFORTABLE_MIN_HEIGHT = 560;

// A tier only changes once the panel is this far past a threshold. Each tier
// changes padding and font size, which can nudge the measured size by a few
// pixels; without this band a panel sitting on a threshold flips back and
// forth.
const HYSTERESIS_PX = 24;

const getTier = (width, height, previous) => {
  if (!width || !height) return previous;

  // Leaving a tier requires crossing its threshold by the full band; entering
  // uses the plain threshold.
  const compactSlack = previous === "compact" ? HYSTERESIS_PX : 0;
  if (
    width <= COMPACT_MAX_WIDTH + compactSlack ||
    height <= COMPACT_MAX_HEIGHT + compactSlack
  ) {
    return "compact";
  }

  const comfortableSlack = previous === "comfortable" ? HYSTERESIS_PX : 0;
  if (
    width >= COMFORTABLE_MIN_WIDTH - comfortableSlack &&
    height >= COMFORTABLE_MIN_HEIGHT - comfortableSlack
  ) {
    return "comfortable";
  }

  return "default";
};

export const useResponsiveTileTypography = () => {
  const containerRef = useRef(null);
  const [tier, setTier] = useState("default");

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    // Measure the border box, not the content box: tiers change padding, so
    // the content box would shift as a result of the tier switch itself.
    const update = () => {
      setTier((previous) =>
        getTier(Math.round(node.offsetWidth), Math.round(node.offsetHeight), previous),
      );
    };

    update();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(update);
    observer.observe(node, { box: "border-box" });
    return () => observer.disconnect();
  }, []);

  return { containerRef, tier };
};
